import { useEffect, useState } from "react";
import { api, notifyError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Save, Loader2, CreditCard, AlertTriangle, RefreshCw, Copy, Link2 } from "lucide-react";

const empty = {
  default_provider: "stripe",
  stripe_enabled: true,
  paytr_enabled: false,
  paytr_merchant_id: "",
  paytr_merchant_key: "",
  paytr_merchant_salt: "",
  paytr_test_mode: true,
};

export default function MasterPaymentSettings() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  const webhookUrl = `${window.location.origin}/api/webhook/paytr`;
  const healthUrl = `${window.location.origin}/api/healthz`;

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopyalandı`);
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const recheckPending = async () => {
    setRechecking(true);
    try {
      const { data } = await api.post("/master/billing/recheck-pending");
      toast.success(
        `${data.checked} bekleyen işlem sorgulandı — ${data.confirmed} onaylandı, ${data.failed} başarısız, ${data.unknown} hala belirsiz`,
        { duration: 8000 }
      );
    } catch (err) {
      notifyError(err);
    } finally {
      setRechecking(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/master/payment-settings");
      setForm({
        ...empty,
        ...data,
        paytr_merchant_id: data.paytr_merchant_id || "",
        paytr_merchant_key: data.paytr_merchant_key || "",
        paytr_merchant_salt: data.paytr_merchant_salt || "",
      });
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/master/payment-settings", form);
      toast.success("Ayarlar kaydedildi");
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto space-y-6" data-testid="master-payment-settings-page">
      <div>
        <div className="label-eyebrow text-amber-600">Yapılandırma</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Ödeme Ayarları</h1>
        <p className="text-sm text-muted-foreground mt-2">
          İşletmelerin abonelik ödemesinde kullanacağı sağlayıcıyı ve API bilgilerini buradan yönetin.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="payment-settings-form">
        {/* Default provider */}
        <Card className="rounded-sm border-border p-6 space-y-4">
          <div>
            <Label className="label-eyebrow">Varsayılan Ödeme Yöntemi</Label>
            <p className="text-xs text-muted-foreground mt-1">Tenant'lar abonelik alırken bu yöntem ön seçili olur.</p>
          </div>
          <Select value={form.default_provider} onValueChange={(v) => setForm({ ...form, default_provider: v })}>
            <SelectTrigger className="rounded-sm" data-testid="default-provider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="stripe">Stripe (Test/Canlı)</SelectItem>
              <SelectItem value="paytr">PayTR (Türkiye)</SelectItem>
            </SelectContent>
          </Select>
        </Card>

        {/* Stripe */}
        <Card className="rounded-sm border-border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#635BFF] text-white flex items-center justify-center">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg tracking-tight">Stripe</div>
                <div className="text-xs text-muted-foreground">Uluslararası kart ödemeleri</div>
              </div>
            </div>
            <Switch
              checked={form.stripe_enabled}
              onCheckedChange={(v) => setForm({ ...form, stripe_enabled: v })}
              data-testid="stripe-enabled"
            />
          </div>
          <div className="bg-muted/50 border border-border rounded-sm p-3 text-xs text-muted-foreground">
            Stripe API key, sunucu environment'ında <code className="font-mono">STRIPE_API_KEY</code> ile yönetiliyor.
            Şu an <strong>test modunda</strong> aktif.
          </div>
        </Card>

        {/* PayTR */}
        <Card className="rounded-sm border-border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#0E8A7C] text-white flex items-center justify-center">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg tracking-tight">PayTR</div>
                <div className="text-xs text-muted-foreground">Türkiye yerel ödeme sağlayıcısı</div>
              </div>
            </div>
            <Switch
              checked={form.paytr_enabled}
              onCheckedChange={(v) => setForm({ ...form, paytr_enabled: v })}
              data-testid="paytr-enabled"
            />
          </div>

          {!form.paytr_merchant_id && form.paytr_enabled && (
            <div className="border-l-2 border-amber-500 bg-amber-500/10 p-3 text-xs flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                <strong>MOCKED:</strong> PayTR canlı API bilgileriniz yok. Şu an simüle edilen ödeme akışı çalışıyor; gerçek tahsilat olmaz. Aşağıya merchant bilgilerinizi girip kaydedin.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Merchant ID</Label>
              <Input
                value={form.paytr_merchant_id}
                onChange={(e) => setForm({ ...form, paytr_merchant_id: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="paytr-merchant-id"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Merchant Key</Label>
              <Input
                type="password"
                value={form.paytr_merchant_key}
                onChange={(e) => setForm({ ...form, paytr_merchant_key: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="paytr-merchant-key"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="label-eyebrow">Merchant Salt</Label>
              <Input
                type="password"
                value={form.paytr_merchant_salt}
                onChange={(e) => setForm({ ...form, paytr_merchant_salt: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="paytr-merchant-salt"
              />
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-border pt-3">
            <Label className="label-eyebrow">PayTR Test Modu</Label>
            <Switch
              checked={form.paytr_test_mode}
              onCheckedChange={(v) => setForm({ ...form, paytr_test_mode: v })}
              data-testid="paytr-test-mode"
            />
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-amber-600" />
              <Label className="label-eyebrow">PayTR Bildirim URL'i</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              PayTR merchant paneli → <strong>Yönetim & Ayarlar → API Entegrasyonu</strong> → "Bildirim URL" alanına aşağıdaki adresi girin. PayTR ödeme tamamlandığında bu URL'e POST atar, <code>OK</code> yanıtı bekler. Yanlış URL → ödeme askıda kalır.
            </p>
            <div className="flex items-center gap-2">
              <Input value={webhookUrl} readOnly className="rounded-sm font-mono text-xs" data-testid="paytr-webhook-url" />
              <Button
                type="button"
                variant="outline"
                onClick={() => copyToClipboard(webhookUrl, "Bildirim URL")}
                className="rounded-sm shrink-0"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Önce <a href={healthUrl} target="_blank" rel="noreferrer" className="underline">{healthUrl}</a> adresini tarayıcıda açın — <code>{`{"ok":true}`}</code> dönmeli. HTML (örn. WordPress anasayfası) dönüyorsa <strong>backend bu domain'de değil</strong>, PayTR de aynı yanıtı alacak.
            </p>
          </div>

          <div className="border-t border-border pt-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="label-eyebrow">Bekleyen İşlemleri Yeniden Sorgula</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Webhook ulaşmadıysa PayTR'a "durum-sorgu" API'siyle sorar, başarılıları paket atamasıyla kapatır.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={recheckPending}
                disabled={rechecking}
                className="rounded-sm shrink-0"
                data-testid="paytr-recheck-pending"
              >
                {rechecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-2" /> Yeniden Sorgula</>}
              </Button>
            </div>
          </div>
        </Card>

        <Button type="submit" disabled={saving} className="w-full sm:w-auto rounded-sm bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="payment-settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Ayarları Kaydet</>}
        </Button>
      </form>
    </div>
  );
}
